import {
  GetQuestionCreationTaskByIdDocument,
  OrderType,
  useSearchUsersByAppRoleFiltersLazyQuery,
  useUpdateQuestionCreationTaskMutation,
} from '@exam-management/graphql';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
  FormModal,
  FormSearchSelection,
  PersonOutlinedIcon,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { useQuestionCreationTaskDetails } from '../../providers';

import { type ChangeAssigneeModalFormValues } from './schema';

interface ChangeAssigneeModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ChangeAssigneeFormModal(props: Readonly<ChangeAssigneeModalProps>) {
  const { open, onClose } = props;
  const { task } = useQuestionCreationTaskDetails();
  const { showSnackbar } = useSnackbar();

  const formContext = useFormContext<ChangeAssigneeModalFormValues>();

  const [searchUser, { data: userListData, loading: isSearchingUserList }] =
    useSearchUsersByAppRoleFiltersLazyQuery();

  const handleSearchUser = (inputValue: string | null) => {
    const [firstname, lastname] = inputValue?.trim().split(' ') ?? [];
    searchUser({
      variables: {
        appRoleNames: [],
        order: {
          type: OrderType.Asc,
          field: 'firstname',
        },
        paging: {
          currentPage: 0,
          pageSize: 100,
        },
        search: {
          firstname: firstname,
          lastname: lastname,
          email: '',
          status: '',
        },
      },
      fetchPolicy: 'network-only',
    });
  };

  const userOptions = useMemo(() => {
    if (isSearchingUserList || !userListData) return [];

    const { users } = userListData.UsBoSearchUsersWithAppRoleFilters;

    return users.map((user) => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user.id,
    }));
  }, [isSearchingUserList, userListData]);

  const [updateAssignee] = useUpdateQuestionCreationTaskMutation();

  const onSubmit = async (data: ChangeAssigneeModalFormValues) => {
    await updateAssignee({
      variables: {
        updateQuestionCreationTaskInput: {
          taskId: task.id,
          name: task.name as string,
          assigneeId: data.assignee.value,
          dueDate: task.dueDate,
        },
      },
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showSnackbar({
          message: 'Task has been saved',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'Failed to save task',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  const handleClose = () => {
    formContext.reset();
    onClose();
  };

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext]);

  return (
    <FormModal open={open} onClose={handleClose} maxWidth='sm'>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>
        <DialogTitle onClose={handleClose}>Change Assignee </DialogTitle>
        <Divider />
        <DialogContent>
          <FormSearchSelection
            name='assignee'
            label='Assignee'
            mode='fetch-debounce'
            options={userOptions}
            placeholder='กรุณาเลือก Assignee'
            loading={isSearchingUserList}
            required
            onSearch={handleSearchUser}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <PersonOutlinedIcon
                    sx={(theme) => ({
                      fontSize: '20px',
                      mr: theme.spacing(2),
                      color: theme.palette.grey[500],
                    })}
                  />
                ),
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={handleClose}>
            Cancel
          </Button>
          <FormButton label='Change Assignee' type='submit' />
        </DialogActions>
      </Form>
    </FormModal>
  );
}
